import React, { useState, useRef, useEffect } from 'react';
import { Eye, Crown } from 'lucide-react';
import { getModelIds, MODEL_INFO, hasVisionCapability, getModelMultiplier, isModelAvailable } from '../constants/modelConstants';

type ModelSelectorProps = {
  selectedModel: string;
  useVision: boolean;
  onModelChange: (model: string, useVision: boolean) => void;
};

const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel,
  useVision,
  onModelChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get only available model IDs from the constants
  const availableModelIds = getModelIds();

  // Check if the currently selected model is available
  const isSelectedModelAvailable = isModelAvailable(selectedModel);

  // Render strength crowns based on multiplier value (directly using the value)
  const renderStrengthCrowns = (multiplier: number) => {
    const totalCrowns = 5;
    const fullCrowns = Math.floor(multiplier);
    const hasHalfCrown = multiplier % 1 >= 0.5;
    
    return (
      <div className="flex items-center">
        {[...Array(totalCrowns)].map((_, index) => {
          // Full crown
          if (index < fullCrowns) {
            return <Crown key={index} size={12} className="text-terminal-green" />;
          }
          // Half crown
          else if (index === fullCrowns && hasHalfCrown) {
            return (
              <div key={index} className="relative" style={{ width: '12px', height: '12px' }}>
                {/* Background half (dimmed) */}
                <Crown size={12} className="absolute text-terminal-green/30" />
                {/* Foreground half (bright) */}
                <div className="absolute overflow-hidden" style={{ width: '6px', height: '12px' }}>
                  <Crown size={12} className="text-terminal-green" />
                </div>
              </div>
            );
          }
          // Empty crown
          else {
            return <Crown key={index} size={12} className="text-terminal-green/30" />;
          }
        })}
      </div>
    );
  };

  // Handle model selection
  const handleSelectModel = (modelId: string, withVision: boolean) => {
    // Only allow selection of available models
    if (isModelAvailable(modelId)) {
      onModelChange(modelId, withVision);
      setIsOpen(false);
    }
  };

  // Get the currently selected display name
  const currentDisplayName = MODEL_INFO[selectedModel]?.displayName || 'Select Model';
  
  return (
    <div className="flex flex-col items-start gap-3 w-full">
      <label className="text-left font-vt323 text-xl">$ SELECT MODEL:</label>
      <div ref={dropdownRef} className="relative w-full">
        {/* Selected model display */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full bg-bg-dark text-terminal-green border border-terminal-green p-2 font-jetbrains cursor-pointer hover:bg-dark-green flex justify-between items-center ${
            !isSelectedModelAvailable ? 'opacity-50' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <span>
              {currentDisplayName}
              {!isSelectedModelAvailable && ' (Unavailable)'}
            </span>
            {useVision && hasVisionCapability(selectedModel) && (
              <Eye size={16} className="text-terminal-green" />
            )}
          </div>
          <div className="flex items-center">
            {renderStrengthCrowns(getModelMultiplier(selectedModel, useVision))}
          </div>
        </button>

        {/* Dropdown options - only show available models */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-bg-dark border border-terminal-green shadow-lg max-h-60 overflow-auto">
            {availableModelIds.map((modelId) => {
              const modelInfo = MODEL_INFO[modelId];
              const hasVision = hasVisionCapability(modelId);
              
              return (
                <React.Fragment key={modelId}>
                  {/* Base model option */}
                  <div
                    onClick={() => handleSelectModel(modelId, false)}
                    className={`p-2 cursor-pointer hover:bg-dark-green ${selectedModel === modelId && !useVision ? 'bg-dark-green' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate mr-2">{modelInfo.displayName}</span>
                      <div className="flex-shrink-0 flex items-center">
                        {renderStrengthCrowns(modelInfo.scoreMultiplier)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Vision option if available */}
                  {hasVision && (
                    <div
                      onClick={() => handleSelectModel(modelId, true)}
                      className={`p-2 cursor-pointer hover:bg-dark-green ${selectedModel === modelId && useVision ? 'bg-dark-green' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 truncate mr-2">
                          <span>{modelInfo.displayName}</span>
                          <Eye size={16} className="text-terminal-green flex-shrink-0" />
                        </div>
                        <div className="flex-shrink-0 flex items-center">
                          {renderStrengthCrowns(modelInfo.visionMultiplier)}
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSelector;